import React, { useEffect, useRef, useState, useMemo } from "react";
import DataTable from "react-data-table-component";
import {
  AiOutlineDownload,
  AiOutlineEdit,
  AiOutlineCheckCircle,
  AiOutlineClose,
  AiOutlineFileText,
} from "react-icons/ai";

// ---- REAL API Section ----
import userAuthService from '../services/userAuthService';

export async function getDraftCopyDetails() {
  const response = await userAuthService.userApiCall('/tax-returns/draft/');
  
  if (!response.ok) {
    throw new Error('Failed to fetch draft copies');
  }
  
  const data = await response.json();
  return data.data || [];
}

export async function downloadDraftFile({ id, filename }) {
  const response = await userAuthService.userApiCall(`/tax-returns/draft/${id}/download/`);
  
  if (!response.ok) {
    throw new Error('Failed to download draft file');
  }
  
  const blob = await response.blob();
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  window.URL.revokeObjectURL(url);
  document.body.removeChild(a);
}

export async function editDraftCopyFile({ id, newFile }) {
  const formData = new FormData();
  formData.append('file', newFile);
  
  const response = await userAuthService.userApiCall(`/tax-returns/draft/${id}/edit/`, {
    method: 'PUT',
    body: formData
  });
  
  if (!response.ok) {
    throw new Error('Failed to edit draft copy');
  }
  
  return await response.json();
}

export async function submitDraftCopy({ id }) {
  const response = await userAuthService.userApiCall(`/tax-returns/draft/${id}/submit/`, {
    method: 'POST'
  });
  
  if (!response.ok) {
    throw new Error('Failed to submit draft copy');
  }
  
  return await response.json();
}

export default function DownloadTaxReturnDraftCopy() {
  const [data, setData] = useState([]);
  const [perPage, setPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [editDraft, setEditDraft] = useState(null);
  const [editFile, setEditFile] = useState(null);
  const [editError, setEditError] = useState("");
  const [submitDraft, setSubmitDraft] = useState(null);
  const [successPopup, setSuccessPopup] = useState(null);

  const editFileInput = useRef();

  // Format date to CST timezone
  function formatToCST(dateString) {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      timeZone: 'America/Chicago',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    });
  }

  async function fetchData() {
    setLoading(true);
    const serverData = await getDraftCopyDetails();
    
    // Process the data to add required fields
    // Use type_display from API (which includes category prefix: "Draft Copy", "Amendment Draft Copy", or "Notice Draft Copy")
    // IMPORTANT: Preserve is_submitted status from API - each tax return has its own status
    const processedData = serverData.map(item => ({
      ...item,
      category: item.type_display || "Draft Copy", // Use type_display from API (includes category prefix)
      datetime: formatToCST(item.created_at), // Convert to CST timezone
      filename: item.filename || 'Unknown file', // Ensure filename exists
      // Preserve is_submitted from API - this is specific to each tax return (regular vs amendment are separate)
      is_submitted: item.is_submitted || false
    }));
    
    setData(processedData);
    setLoading(false);
  }
  useEffect(() => { fetchData(); }, []);

  const columns = useMemo(() => [
    {
      name: "S.No",
      width: "80px",
      cell: (_row, idx) => (currentPage - 1) * perPage + idx + 1,
      style: { fontWeight: 600, color: "#4338ca" },
    },
    { name: "Category", selector: row => row.category, sortable: true, style: { fontWeight: 500 }, wrap: true },
    { name: "Year", selector: row => row.year, sortable: true, width: "100px", style: { textAlign: "center" } },
    {
      name: "Status",
      selector: row => row.is_submitted ? "Submitted" : "Pending",
      sortable: true,
      width: "120px",
      cell: row => (
        <span style={{
          background: row.is_submitted ? "#059669" : "#f59e0b",
          color: "#fff",
          padding: "4px 8px",
          borderRadius: "4px",
          fontSize: "12px",
          fontWeight: "500",
          textAlign: "center",
          display: "inline-block",
          minWidth: "70px"
        }}>
          {row.is_submitted ? "Submitted" : "Pending"}
        </span>
      ),
      style: { textAlign: "center" }
    },
    {
      name: "File Name",
      selector: row => row.filename,
      sortable: true,
      cell: row => (
        <button
          onClick={() => handleDownload(row)}
          style={{
            color: "#2563eb", background: "none", border: "none",
            fontWeight: 600, cursor: "pointer", fontSize: 14,
            display: "flex", alignItems: "center", gap: 5
          }}
          title={`Download ${row.filename}`}
          disabled={loading}
        >
          <AiOutlineDownload />{row.filename}
        </button>
      ),
      wrap: true,
    },
    {
      name: "Date Time",
      selector: row => row.datetime,
      sortable: true,

      style: { color: "#555", minWidth: "120px", fontSize: 14 },
      wrap: true,
    },
    {
      name: "Actions",
      cell: row => (
        <div style={{ display: "flex", gap: 10 }}>
          <button
            onClick={() => openEdit(row)}
            aria-label="Edit draft"
            style={{
              background: "#a1a1aa", // Always disabled
              color: "#fff", borderRadius: 6, border: "none",
              padding: "6px 12px", fontSize: 13,
              cursor: "not-allowed",
              display: "flex", alignItems: "center", gap: 4, fontWeight: 500
            }}
            disabled={true} // Always disabled
            title="Edit functionality disabled"
          >
            <AiOutlineEdit /> Edit
          </button>
          <button
            onClick={() => openSubmit(row)}
            aria-label="Submit draft"
            style={{
              background: (row.submit || row.is_submitted) ? "#a1a1aa" : "#059669",
              color: "#fff", borderRadius: 6, border: "none",
              padding: "6px 12px", fontSize: 13,
              cursor: (row.submit || row.is_submitted) ? "not-allowed" : "pointer",
              display: "flex", alignItems: "center", gap: 4, fontWeight: 500
            }}
            disabled={row.submit || row.is_submitted}
            title={row.is_submitted ? "Draft already submitted. Submission disabled." : (row.submit ? "Draft submitted. Submission disabled." : "Submit draft")}
          >
            <AiOutlineCheckCircle /> Submit
          </button>
        </div>
      ),
      ignoreRowClick: true,
    }
  ], [currentPage, perPage, loading]);

  async function handleDownload(row) { 
    setLoading(true); 
    try {
      await downloadDraftFile({ id: row.id, filename: row.filename }); 
    } catch (error) {
      console.error('Download failed:', error);
    }
    setLoading(false); 
  }
  function openEdit(row) {
    // Edit functionality is disabled
    return;
  }
  function closeEdit() {
    setEditDraft(null);
    setEditFile(null);
    setEditError("");
    // Also clear file input visually on modal close
    setTimeout(() => { if (editFileInput.current) editFileInput.current.value = ""; }, 50);
  }
  function onEditFileChange(e) {
    const selected = e.target.files && e.target.files[0];
    const allowedTypes = [
      "application/pdf",
      "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      "text/plain",
    ];
    if (!selected) {
      setEditFile(null);
      setEditError("");
      return;
    }
    if (!allowedTypes.includes(selected.type)) {
      setEditFile(null);
      setEditError("Allowed file types: pdf, doc, docx, txt");
      return;
    }
    if (selected.size > 5 * 1024 * 1024) {
      setEditFile(null);
      setEditError("File size must be less than 5MB");
      return;
    }
    setEditFile(selected);
    setEditError("");
  }
  async function handleEditSave(e) {
    e.preventDefault();
    if (!editFile) {
      setEditError("Please select a file");
      return;
    }
    setLoading(true);
    await editDraftCopyFile({ id: editDraft.id, newFile: editFile });
    await fetchData();
    setLoading(false);
    closeEdit();
    setSuccessPopup("Changes saved successfully!");
  }
  function openSubmit(row) { setSubmitDraft(row); }
  function closeSubmit() { setSubmitDraft(null); }
  async function handleSubmitDraft() {
    setLoading(true);
    await submitDraftCopy({ id: submitDraft.id });
    await fetchData();
    setLoading(false);
    setSuccessPopup("Draft copy submitted successfully.");
    closeSubmit();
  }
  function handlePageChange(page) { setCurrentPage(page); }
  function handlePerRowsChange(newPerPage, page) {
    setPerPage(newPerPage);
    setCurrentPage(page);
  }

  const customStyles = {
    rows: { style: { fontSize: 15, background: "#f8fafc" } },
    headRow: { style: { fontWeight: 700, fontSize: 15, background: "#eceffd", color: "#4338ca", minHeight: "46px" } },
    headCells: { style: { borderBottom: "1.5px solid #a5b4fc", paddingLeft: "18px", paddingRight: "18px" } },
    cells: { style: { paddingLeft: "18px", paddingRight: "18px" } },
    pagination: { style: { borderTop: "solid 1px #eceffd", color: "#4338ca", fontWeight: 600 } }
  };

  function SuccessModal({ message, onClose }) {
    return (
      <div
        role="alertdialog"
        aria-modal="true"
        style={{
          position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: "rgba(0,0,0,0.25)",
          display: "flex", justifyContent: "center", alignItems: "center", zIndex: 10001, padding: 24,
        }}
        onClick={onClose}
      >
        <div
          onClick={e => e.stopPropagation()}
          style={{
            backgroundColor: "#f4f7ff",
            padding: 24, borderRadius: 12,
            boxShadow: "0 10px 50px #a5b4fc33",
            maxWidth: 320, width: "100%", textAlign: "center", userSelect: "none",
          }}
        >
          <AiOutlineCheckCircle
            size={60}
            color="#059669"
            style={{ marginBottom: 18 }}
            aria-hidden="true"
          />
          <p style={{ fontWeight: 700, fontSize: 18, color: "#059669" }}>{message}</p>
          <button
            onClick={onClose}
            style={{
              marginTop: 20, padding: "8px 28px", borderRadius: 8, border: "none",
              backgroundColor: "#059669", color: "#fff", fontWeight: 700,
              cursor: "pointer", fontSize: 14, userSelect: "none",
            }}
          >Close</button>
        </div>
      </div>
    );
  }
  function Modal({ onClose, title, children }) {
    return (
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="modalTitle"
        style={{
          position: "fixed", top: 0, left: 0, right: 0, bottom: 0, zIndex: 10000,
          background: "rgba(0,0,0,0.26)",
          display: "flex", alignItems: "center", justifyContent: "center"
        }}
        tabIndex={-1}
        onClick={onClose}
      >
        <div
          onClick={e => e.stopPropagation()}
          style={{
            background: "#f4f7ff", borderRadius: 13, position: "relative",
            padding: "38px 36px 18px 36px", minWidth: 325,
            boxShadow: "0 10px 50px #a5b4fc33",
          }}
        >
          <h3 id="modalTitle" style={{ fontWeight: 700, color: "#2d3393", margin: 0, marginBottom: 17, fontSize: 21 }}>
            {title}
          </h3>
          {children}
          <button
            aria-label="Close"
            onClick={onClose}
            style={{
              position: "absolute", top: 14, right: 14, background: "transparent",
              border: "none", fontSize: 24, cursor: "pointer", color: "#555", userSelect: "none",
            }}
          >
            <AiOutlineClose />
          </button>
        </div>
      </div>
    );
  }
  function modalBtn(bg, color) {
    return {
      padding: "9px 22px", background: bg, color, border: "none",
      borderRadius: 8, fontWeight: 600, fontSize: 15, cursor: "pointer",
    };
  }

  return (
    <section style={{ userSelect: "none" }}>
      <h2
        style={{
          // fontWeight: 800,
          // color: "#4338ca",
          // fontSize: 24,
          // marginBottom: 22,
          // letterSpacing: "0.02em",
          // display: "flex",
          // alignItems: "center",
          // gap: 10,

          fontSize: 38,
          marginRight: 10,
          color: "#4338ca",
          verticalAlign: "middle",
          filter: "drop-shadow(0px 1px 4px #b5beff77)",
        }}
      >
        <AiOutlineFileText /> Download Draft Copy
      </h2>
      <div style={{
        background: "#fff",
        borderRadius: 15,
        boxShadow: "0 5px 24px #a5b4fc22",
        padding: 20,
      }}>
        <DataTable
          columns={columns}
          data={data}
          highlightOnHover
          striped
          pagination
          paginationPerPage={perPage}
          paginationRowsPerPageOptions={[10, 20, 30, 40, 50]}
          onChangeRowsPerPage={handlePerRowsChange}
          onChangePage={handlePageChange}
          paginationDefaultPage={currentPage}
          progressPending={loading}
          noHeader
          responsive
          persistTableHead
          customStyles={customStyles}
        />
      </div>
      {/* Edit Modal */}
      {editDraft && (
        <Modal title="Edit Draft Copy" onClose={closeEdit}>
          <form onSubmit={handleEditSave} style={{ display: "flex", flexDirection: "column", gap: 17 }}>
            <div><strong>Category: </strong>{editDraft.category}</div>
            <div><strong>Year: </strong>{editDraft.year}</div>
            <div>
              <label style={{ fontWeight: 600, marginBottom: 6, marginTop: 4, display: "block" }}>
                Choose New File
              </label>
              <input
                type="file"
                ref={editFileInput}
                accept=".pdf,.doc,.docx,.txt"
                onChange={onEditFileChange}
                style={{ padding: "7px 0", fontSize: 15 }}
              />
              {editFile &&
                <div style={{ marginTop: 6, fontSize: 14, color: "#2563eb" }}>
                  Selected file: <em>{editFile.name}</em>
                </div>
              }
              {editError &&
                <span style={{ color: "#ef4444", fontWeight: 600 }}>{editError}</span>
              }
            </div>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 15 }}>
              <button type="button" onClick={closeEdit} style={modalBtn("#e5e7eb", "#4b5563")}>
                Close
              </button>
              <button type="submit" style={modalBtn("#6366f1", "#fff")}>
                Save
              </button>
            </div>
          </form>
        </Modal>
      )}
      {/* Submit Modal */}
      {submitDraft && (
        <Modal title="Submit Draft Copy" onClose={closeSubmit}>
          <div style={{ fontSize: 15, fontWeight: 500, marginBottom: 20 }}>
            Are you sure you want to submit <strong>{submitDraft.filename}</strong>?
          </div>
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 15 }}>
            <button onClick={closeSubmit} style={modalBtn("#e5e7eb", "#4b5563")}>
              Cancel
            </button>
            <button
              onClick={handleSubmitDraft}
              style={modalBtn("#059669", "#fff")}
              disabled={submitDraft.submit}
            >
              OK
            </button>
          </div>
        </Modal>
      )}
      {/* Success Popup Modal */}
      {successPopup && (
        <SuccessModal message={successPopup} onClose={() => setSuccessPopup(null)} />
      )}
    </section>
  );
}
